<script>
	import thumb_xDJHtJIX5CE from '$lib/Components/Youtube/images/xDJHtJIX5CE.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import historicCraftsmanHome from './images/historicCraftsmanHome.jpg';
	import indowWindowInsertsInHistoricWindows from './images/indowWindowInsertsInHistoricWindows.jpg';
</script>

<Section>
	<Image
		alt="Historic craftsman home with Indow storm windows installed: Inserts don’t harm old home’s windows."
		src={historicCraftsmanHome}
	/>
	<h2>Alternative to Historic Storm Windows</h2>
	<p>
		Indow window inserts are an attractive, easy-to-use interior storm window.
		Original windows are important to the architectural character of historic
		buildings. Unfortunately, they’re also a top source of energy loss. The
		drafts can make you feel uncomfortable, but also drive up energy costs.
		While exterior storm windows provide some energy efficiency benefits to your
		older home, they’re difficult to use and often unsightly. Indow window
		inserts are an alternative to exterior storm windows.
	</p>

	<Image
		src="/static/routes/resources/blog/storm-window-alternative/window-inserts.gif"
		alt="Diagram of how to install indow insulating window inserts"
		width="341"
		height="400"
	/>
</Section>

<Section>
	<h2>The Problem with Replacing Your Historic Windows</h2>
	<p>
		The Illinois Historic Preservation Agency put together a great report on why
		you should use <a
			href="http://www.state.il.us/hpa/ps/images/replacement_windows.pdf"
			target="_blank"
			rel="noreferrer">storm windows for historic homes</a
		>. It outlines the harm that can come to a historic home if the owner
		replaces the windows instead of restoring them.
	</p>
	<Bubble>
		{#snippet titleSlot()}
			<p>Use interior storm windows for old houses because of:</p>
		{/snippet}
		<ul>
			<li>
				<strong>Sustainability</strong>: Tearing out old windows and putting
				them in landfills harms the environment.
			</li>
			<li>
				<strong>Aesthetics</strong>: New windows cannot mimic the look of
				historic windows or use same materials like old-growth wood. Indow
				inserts are interior storms that both look good and are barely
				noticeable.
			</li>
			<li>
				<strong>Maintenance</strong>: Many new window materials (vinyl,
				fiberglass, sealants) are difficult or impossible to recycle. Indow
				inserts come with a limited lifetime warranty and do not require any
				maintenance – just occasional cleaning like all windows.
			</li>
			<li>
				<strong>Cost</strong>: High quality replacement windows cost three times
				as much as restoring original windows.
			</li>
			<li>
				<strong>Ease</strong>: Measuring for, receiving, and installing Indow
				inserts can typically be done with only one person and requires around a
				combined time of around 15 minutes per window.
			</li>
		</ul>
	</Bubble>
	<p>
		Historic storm windows are one solution to avoid <a
			href="/resources/storm-windows-guide/replacement"
			>doing a full replacement</a
		>. Unfortunately, many storm windows
		<a href="/resources/storm-windows-guide/traditional"
			>require installing a track system</a
		> on the window frame. This not only alters the frame, but isn’t aesthetically
		pleasing.
	</p>
	<Image
		alt="Indow window inserts in historic windows to improve performance without harming the historic value"
		src={indowWindowInsertsInHistoricWindows}
	/>
</Section>

<Section>
	<h2>The Storm Window Alternative</h2>
	<p>
		Indow inserts are custom storm windows for historic homes. Indow uses
		proprietary measurement verification software to ensure a perfect fit – up
		to 1/32’’, even in out-of-square windows. The simple and elegant design
		makes them hardly noticeable once installed. Unlike other storm windows,
		Indow inserts fit without any modifications to the window frame, and are
		held in place with our patented silicone compression tube around the outer
		edge. This means your old, historic windows will perform like new windows
		without losing their historic value.
	</p>
	<p>
		If your historic home needs an alternative to replacements or clunky storm
		windows, contact us today!
	</p>
	<Youtube posterImageSrc={thumb_xDJHtJIX5CE} videoId="xDJHtJIX5CE" />
</Section>

<Section>
	<h2>More Resources: Storm Windows for Historic Homes</h2>
	<Bubble backgroundColor="transparent">
		<nav>
			<ul>
				<li>
					<a href="/resources/storm-windows-guide/replacement"
						>Storm Window vs Replacement Window Guide</a
					>
				</li>
				<li>
					<a href="/resources/blog/performance-storm-windows-regular-windows">
						Performance of Storm Windows vs Regular Windows
					</a>
				</li>
				<li>
					<a href="/resources/storm-windows-guide/traditional">
						Interior vs Exterior Storm Window Guide
					</a>
				</li>
			</ul>
		</nav>
	</Bubble>
</Section>

<Reviews />
